import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popover, Search, List, Card, Icon, TRIGGER_ACTION } from "@theorgchartdev/storybook";
import * as StateLib from "@/Utils/StateLib";
import { GetSearchResults } from "@/Utils/Promises";
import { appstr } from "@coreapp/utils/appstr";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { enumHierarchyTypes } from "@coreapp/constants";
import { useCursorPaginatedSearch } from "@/hooks/useCursorPaginatedSearch";

const NO_PREVIEW_VALUE = "none";
const NO_PREVIEW_LABEL = appstr("No preview");
const PLACEHOLDER_LABEL = appstr("Select a preview");
const SEARCH_PREFIX = <Icon name="user" size="small" color="secondary" />;
const NO_PREVIEW_OPTION = {
  label: NO_PREVIEW_LABEL,
  value: NO_PREVIEW_VALUE,
  className: "preview-person-select__no-preview"
};
const getProjectFields = layout => {
  if (!layout.GetNumCells) return [];
  const fields = new Set();
  const numCells = layout.GetNumCells();
  for (let i = 0; i < numCells; i++) {
    const fieldName = layout.GetIndexCellDesc(i).GetFieldSaveName();
    if (fieldName) fields.add(fieldName);
  }
  return [...fields];
};

const buildCustomChartOptions = layout => {
  const doc = StateLib.GetDoc();
  if (!doc) return [NO_PREVIEW_OPTION];
  const isPosition = layout.GetHierarchyType() === enumHierarchyTypes.positions;
  const objClass = isPosition ? doc.GetPositionClass() : doc.GetBoxClass();
  if (!objClass) return [NO_PREVIEW_OPTION];

  const objects = [];
  const count = objClass.GetObjectCount();
  for (let i = 0; i < count; i++) {
    objects.push(objClass.GetIndexObject(i));
  }

  const options = [NO_PREVIEW_OPTION];

  const personOptions = [];
  for (let i = 0; i < objects.length; i++) {
    const dbObj = objects[i];
    const label = dbObj.FormatEmployeeLabel();
    if (label) {
      personOptions.push({ label, value: dbObj.GetObjectRecordID(), dbObj });
    }
  }

  personOptions.sort((a, b) => a.label.localeCompare(b.label));
  return options.concat(personOptions);
};

const CustomChartSelect = ({ panel, layout }) => {
  const [selectedLabel, setSelectedLabel] = useState("");
  const [selectedValue, setSelectedValue] = useState(NO_PREVIEW_VALUE);
  const [filteredOptions, setFilteredOptions] = useState([NO_PREVIEW_OPTION]);
  const selectedLabelRef = useRef("");
  const allOptionsRef = useRef([NO_PREVIEW_OPTION]);
  const closingRef = useRef(false);
  const popoverOpenCtrlRef = useRef(null);
  const searchHandleRef = useRef(null);

  // Mount-only: panel is a stable mutable ref from GetCurrentPanel()
  useEffect(() => {
    const opts = buildCustomChartOptions(layout);
    allOptionsRef.current = opts;
    setFilteredOptions(opts);
    if (panel.fSaveOverrides && panel.fPreviewDBObj) {
      const wsMgr = StateLib.GetWSMgr();
      wsMgr.SetPreviewPerson(panel.fPreviewDBObj);
      const label = panel.fPreviewDBObj.FormatEmployeeLabel() || "";
      setSelectedLabel(label);
      selectedLabelRef.current = label;
      setSelectedValue(panel.fPreviewDBObj.GetObjectRecordID());
      StateLib.ReRenderCanvas();
    }
  }, []);

  const selectPerson = dbObj => {
    const wsMgr = StateLib.GetWSMgr();
    wsMgr.SetPreviewPerson(dbObj ?? null);
    const label = dbObj ? dbObj.FormatEmployeeLabel() || "" : "";
    setSelectedLabel(label);
    selectedLabelRef.current = label;
    setSelectedValue(dbObj ? dbObj.GetObjectRecordID() : NO_PREVIEW_VALUE);
    searchHandleRef.current?.setValue(label, true);
    StateLib.ReRenderCanvas();
    popoverOpenCtrlRef.current?.setOpen(false);
  };

  const handleSelect = option => {
    if (option.value === NO_PREVIEW_VALUE) {
      selectPerson(null);
    } else {
      selectPerson(option.dbObj);
    }
  };

  const handlePopoverOpen = () => {
    closingRef.current = true;
    searchHandleRef.current?.setValue("", true);
    closingRef.current = false;
    setFilteredOptions(allOptionsRef.current);
  };

  const handlePopoverClose = () => {
    closingRef.current = true;
    searchHandleRef.current?.setValue(selectedLabelRef.current, true);
    closingRef.current = false;
  };

  const handleSearchInput = query => {
    if (closingRef.current) return;
    const lower = query.toLowerCase();
    setFilteredOptions(
      allOptionsRef.current.filter(
        opt => opt.value === NO_PREVIEW_VALUE || opt.label.toLowerCase().includes(lower)
      )
    );
  };

  return (
    <Popover
      triggerAction={TRIGGER_ACTION.none}
      openCtrlRef={popoverOpenCtrlRef}
      onOpen={handlePopoverOpen}
      onClose={handlePopoverClose}
      reference={
        <Search
          dataTestId="box-layout-editor-toolbar-preview-person-search"
          size="small"
          wrapperDivClass="ble-preview-search"
          placeholder={PLACEHOLDER_LABEL}
          prefix={SEARCH_PREFIX}
          initialValue={selectedLabel}
          onInput={handleSearchInput}
          handleRef={searchHandleRef}
          onFocus={() => popoverOpenCtrlRef.current?.setOpen(true)}
        />
      }
      options={{ placement: "bottom-start" }}
    >
      {({ availableSize }) => (
        <Card withShadow>
          <List
            optionSize="small"
            options={filteredOptions}
            value={selectedValue}
            availableSize={
              availableSize
                ? { ...availableSize, height: `min(${availableSize.height}, 300px)` }
                : availableSize
            }
            parentOpenCtrlRef={popoverOpenCtrlRef}
            onSelect={handleSelect}
            minVirtualItems={1}
            listWidth="large"
            enableKeyboardCursor
          />
        </Card>
      )}
    </Popover>
  );
};

const BaseChartSearchDropdown = ({ panel, layout }) => {
  const [selectedLabel, setSelectedLabel] = useState("");
  const [selectedValue, setSelectedValue] = useState(NO_PREVIEW_VALUE);
  const selectedLabelRef = useRef("");

  const closingRef = useRef(false);
  const popoverOpenCtrlRef = useRef(null);
  const searchHandleRef = useRef(null);

  const doc = StateLib.GetDoc();
  const isPosition = layout.GetHierarchyType() === enumHierarchyTypes.positions;
  const objClass = isPosition ? doc.GetPositionClass() : doc.GetBoxClass();
  const sortField = isPosition ? "Position Title" : "Name";

  const {
    results: rawRecords,
    loading,
    handleFetch: fetchPage,
    handleNextPage,
    reset
  } = useCursorPaginatedSearch({
    searchFn: async (query, { sort, cursor }) => {
      const projectFields = getProjectFields(layout);
      const result = await GetSearchResults(query, {
        chartID: doc.GetIWizData().GetBaseChartID(),
        handlePositions: isPosition,
        sort,
        ...(cursor !== undefined && { cursor }),
        ...(projectFields.length > 0 && { projectFields })
      });
      return result?.result?.results ?? [];
    },
    sort: { fieldName: sortField, order: "asc" }
  });

  // Mount-only: panel is a stable mutable ref from GetCurrentPanel()
  useEffect(() => {
    if (panel.fSaveOverrides && panel.fPreviewDBObj) {
      const wsMgr = StateLib.GetWSMgr();
      wsMgr.SetPreviewPerson(panel.fPreviewDBObj);
      const label = panel.fPreviewDBObj.FormatEmployeeLabel() || "";
      setSelectedLabel(label);
      selectedLabelRef.current = label;
      setSelectedValue(panel.fPreviewDBObj.GetObjectRecordID());
      StateLib.ReRenderCanvas();
    }
  }, []);

  const options = useMemo(() => {
    const personOptions = [];
    for (const record of rawRecords) {
      const dbObj = new QObjDBObject();
      dbObj.InitObject(objClass);
      objClass.ProcessObject(dbObj, record);
      const label = dbObj.FormatEmployeeLabel();
      if (label) {
        personOptions.push({
          label,
          value: dbObj.GetObjectRecordID(),
          dbObj
        });
      }
    }
    return [NO_PREVIEW_OPTION, ...personOptions];
  }, [rawRecords, objClass]);

  const selectPerson = dbObj => {
    const wsMgr = StateLib.GetWSMgr();
    wsMgr.SetPreviewPerson(dbObj ?? null);
    const label = dbObj ? dbObj.FormatEmployeeLabel() || "" : "";
    setSelectedLabel(label);
    selectedLabelRef.current = label;
    setSelectedValue(dbObj ? dbObj.GetObjectRecordID() : NO_PREVIEW_VALUE);
    closingRef.current = true;
    searchHandleRef.current?.setValue(label, true);
    closingRef.current = false;
    StateLib.ReRenderCanvas();
    popoverOpenCtrlRef.current?.setOpen(false);
  };

  const handleScroll = (scrollProps, listOuterRef) => {
    const { scrollDirection } = scrollProps;
    const container = listOuterRef.current;
    if (!container || loading) return;

    const { scrollHeight, scrollTop, clientHeight } = container;
    const isBottom = scrollHeight - scrollTop - clientHeight <= 20;

    if (scrollDirection === "forward" && isBottom) {
      handleNextPage();
    }
  };

  const handleSelect = option => {
    if (option.value === NO_PREVIEW_VALUE) {
      selectPerson(null);
    } else {
      selectPerson(option.dbObj);
    }
  };

  const handlePopoverOpen = () => {
    closingRef.current = true;
    searchHandleRef.current?.setValue("", true);
    closingRef.current = false;
    fetchPage("");
  };

  const handlePopoverClose = () => {
    closingRef.current = true;
    reset();
    searchHandleRef.current?.setValue(selectedLabelRef.current, true);
    closingRef.current = false;
  };

  const handleSearchInput = query => {
    if (closingRef.current) return;
    fetchPage(query);
  };

  return (
    <Popover
      triggerAction={TRIGGER_ACTION.none}
      openCtrlRef={popoverOpenCtrlRef}
      onOpen={handlePopoverOpen}
      onClose={handlePopoverClose}
      reference={
        <Search
          dataTestId="box-layout-editor-toolbar-preview-person-search"
          size="small"
          wrapperDivClass="ble-preview-search"
          placeholder={PLACEHOLDER_LABEL}
          prefix={SEARCH_PREFIX}
          initialValue={selectedLabel}
          onInput={handleSearchInput}
          handleRef={searchHandleRef}
          onFocus={() => popoverOpenCtrlRef.current?.setOpen(true)}
        />
      }
      options={{ placement: "bottom-start" }}
    >
      {({ availableSize }) => (
        <Card withShadow>
          <List
            optionSize="small"
            options={options}
            value={selectedValue}
            availableSize={
              availableSize
                ? { ...availableSize, height: `min(${availableSize.height}, 300px)` }
                : availableSize
            }
            parentOpenCtrlRef={popoverOpenCtrlRef}
            onSelect={handleSelect}
            onVirtualizedListScroll={handleScroll}
            minVirtualItems={1}
            showLoader={loading}
            listWidth="large"
            enableKeyboardCursor
          />
        </Card>
      )}
    </Popover>
  );
};

const PreviewPersonSelect = ({ panel, layout }) => {
  const doc = StateLib.GetDoc();

  if (doc.IsCustomChart()) {
    return <CustomChartSelect panel={panel} layout={layout} />;
  }

  return <BaseChartSearchDropdown panel={panel} layout={layout} />;
};

export default PreviewPersonSelect;
